import clsx from 'clsx';
import Button from 'components/common/Button';
import { FadeIn } from 'components/common/FadeIn';
import { SyntheticEvent, useState } from 'react';
import {
  fetchUtmParamsJoinedWithFbDataFromLocalStorage,
  identifyUser,
  trackMailingListSignup,
} from 'utils/analytics/analytics';
import { tryApi } from 'utils/api/common';
import { authApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import { validEmail } from 'utils/RegistrationValidator';

import styles from './footer.module.css';

const NewsletterSignUp = () => {
  const [email, setEmail] = useState('');
  const [successful, setSuccessful] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showRegistered, setShowRegistered] = useState(false);

  const submitEmail = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);
    setShowRegistered(false);

    if (!validEmail(email)) {
      setErrorMessage('Please provide a valid email address.');
      setLoading(false);
      return;
    }

    const utmParams = fetchUtmParamsJoinedWithFbDataFromLocalStorage();
    // expects Record<string, string> or null
    const formattedUtmParams = utmParams
      ? Object.fromEntries(
          Object.entries(utmParams).map(([key, value]) => [
            key,
            typeof value === 'string' ? value : JSON.stringify(value),
          ]),
        )
      : null;
    const res = await tryApi(
      authApiClient.public.signUpForNewsletterWithDiscount({
        body: {
          emailAddress: email,
          source: 'FOOTER',
          utmParams: formattedUtmParams,
        },
      }),
    );
    setLoading(false);

    if (res.status === HttpStatusCode.OK) {
      setSuccessful(true);
      identifyUser({ emailAddress: email });
      trackMailingListSignup(email);
    } else if (res.status === 'error') {
      setErrorMessage('Unable to sign up to our newsletter, try again later.');
    } else {
      setShowRegistered(true);
    }
  };

  return (
    <div className={styles.signUpHeader}>
      {successful ? (
        <>
          <FadeIn key="success" direction="right" delay={0.2}>
            <h5 className={clsx('daymadeTypographyXL', styles.signUpTitle)}>
              You're in!
            </h5>
            <div
              className={clsx(
                'daymadeTypographySRegular',
                styles.signUpDescription,
              )}
            >
              Welcome to the club - great things are on the way
            </div>
          </FadeIn>
          <div />
        </>
      ) : (
        <>
          <FadeIn onScroll>
            <h5 className={clsx('daymadeTypographyXL', styles.signUpTitle)}>
              Sign up & save
            </h5>
            <div
              className={clsx(
                'daymadeTypographySRegular',
                styles.signUpDescription,
              )}
            >
              Join our newsletter and get{' '}
              <span className={styles.signUpEmphasis}>50% off</span> your first
              order
            </div>
          </FadeIn>
          <FadeIn onScroll delay={0.2}>
            <form className={styles.formSignUp} onSubmit={submitEmail}>
              <input
                className={styles.inputSignUp}
                placeholder="Your email address"
                type="email"
                autoComplete="email"
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />
              <Button
                type="submit"
                buttonStyle="SingleInputForm"
                buttonSize="Large"
                disabled={!email || email.length < 3}
                loading={loading}
              >
                Sign Up
              </Button>
              {errorMessage || showRegistered ? (
                <div
                  className={clsx(
                    showRegistered ? styles.registered : '',
                    styles.errorMessage,
                    'daymadeTypographySRegular',
                  )}
                >
                  {showRegistered
                    ? 'This email address is already registered!'
                    : errorMessage}
                </div>
              ) : null}
            </form>
          </FadeIn>
        </>
      )}
    </div>
  );
};

export default NewsletterSignUp;
